import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Home } from '@mui/icons-material';
import { FormControlLabel, Switch } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  type ApolloClient,
  type NormalizedCacheObject,
  useApolloClient,
} from '@apollo/react-hooks';
import {
  getProcessedJsonFilter,
  PanelApplications,
  ViewSituation,
} from './components';
import { type PanelAppliedFilter } from './components/';

import { panelFundingConfig } from './components/panelApplicantListPermissionConfig';
import { PAGE_TEXT, PANEL_FORM_DIALOG_TEXT } from '../../../lang';
import { GS_ASSESSMENT_MODULE } from '../../../components';
import {
  Alert,
  type AlertDetails,
  Button,
  Dialog,
  Loading,
} from '../../../../../components';
import {
  useGetAllocatePanel,
  useGetPanelApplications,
  useReleasePanelAllocation,
  useUpdatePanelAllocation,
} from '../../../graphql';
import { useAuth } from '../../../../../contexts';
import { type FundingSource } from '../../../../Nomination/components';

type GSPanelDetailsProps = {
  viewSituation: ViewSituation;
};

export const defaultAppliedFilter: PanelAppliedFilter = {
  tags: [],
  panelResults: [],
};

const getInitGSAutoRefresh = (): boolean => {
  const searchParams = new URLSearchParams(location.search);
  const urlFmKey = searchParams.get('autoRefresh') ?? '';
  return urlFmKey === '1';
};

export const GSPanelDetails: React.FunctionComponent<GSPanelDetailsProps> = (
  props: GSPanelDetailsProps,
) => {
  const { viewSituation } = props;
  const { panelId = '' } = useParams();
  const isGSview = viewSituation === ViewSituation.GS;
  const navigate = useNavigate();
  const { handleLoading } = useAuth();
  const [openAlert, setOpenAlert] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  const initARKey = getInitGSAutoRefresh();
  const [gsAutoRefresh, setGsAutoRefresh] = useState(isGSview && initARKey);
  const [tableFilter, setTableFilter] =
    useState<PanelAppliedFilter>(defaultAppliedFilter);

  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const appliedFilter = getProcessedJsonFilter(tableFilter);

  const {
    panelApplications,
    panelInformation,
    panelNumbers,
    dimensionsCollection,
    allocationReleased,
    hasAllocatedReviewers,
    dualPartnerUniversityList,
    lockFilterControllers: { filterLockedFlag, filterToApply },
    loading,
    error,
    refetch,
  } = useGetPanelApplications({
    panelId,
    onlyReviewee: false,
    inputFiler: tableFilter,
    filter: appliedFilter,
    initialLocked: isLocked,
  });
  const { fundingSource, lockFilter } = panelInformation;

  useEffect(() => {
    if (!loading) {
      setTableFilter(filterToApply);
      setIsLocked(filterLockedFlag);
    }
  }, [loading, filterLockedFlag]);

  const { showReviewer } = panelFundingConfig(fundingSource as FundingSource);

  const handleApplyTagFilter = (value: PanelAppliedFilter): void => {
    const {
      tags,
      panelResults,
      priorityOrderRange,
      serialNo,
      expr,
      partnerUniversity,
    } = value;

    setTableFilter({
      tags,
      panelResults,
      partnerUniversity,
      priorityOrderRange,
      serialNo,
      expr,
    });
  };

  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const updatePanelAllocation = useUpdatePanelAllocation();
  const releasePanelAllocation = useReleasePanelAllocation();
  const handleGetPanelApplicationInitialAllocation = async (
    panelId: string,
  ): Promise<void> => {
    await handleLoading(async () => {
      try {
        const allocateResult = await useGetAllocatePanel(client, { panelId });
        const variables = {
          input: {
            panelId,
            entries: allocateResult,
          },
        };
        await updatePanelAllocation({
          variables,
        });
        await refetch();

        handleManageAlert({
          alertContent:
            'Allocated Reviewers Successfully. Please click `Release` button if you have no problem with the results of allocate.',
          alertType: 'success',
        });
      } catch (e) {
        handleManageAlert({
          alertContent: `Error Occurs.` + (e as Error)?.message,
          alertType: 'error',
        });
      }
    });
  };

  const handleReleasePanelAllocation = async (
    panelId: string,
  ): Promise<void> => {
    await handleLoading(async () => {
      try {
        const variables = {
          id: panelId,
        };

        await releasePanelAllocation({
          variables,
        });
        await refetch();

        handleManageAlert({
          alertContent: 'Released Allocation Result Successfully.',
          alertType: 'success',
        });
      } catch (e) {
        handleManageAlert({
          alertContent: `Error Occurs.` + (e as Error)?.message,
          alertType: 'error',
        });
      }
    });
    setOpenConfirmDialog(false);
  };

  const handleBack = (): void => {
    window.location.href = isGSview
      ? `/internal/gs-homepage?am=${GS_ASSESSMENT_MODULE.PANEL_MANAGEMENT}`
      : `/internal/panel-homepage`;
  };

  const handleManageAlert = (alertDetail: AlertDetails): void => {
    setAlertDetails(alertDetail);
    setOpenAlert(true);
  };
  const handleToggleGSAutoRefresh = (): void => {
    setGsAutoRefresh(!gsAutoRefresh);

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('autoRefresh', !gsAutoRefresh ? '1' : '0');
    navigate(`${currentUrl.pathname}${currentUrl.search}`);
  };

  if (error) {
    return (
      <Alert
        openAlert
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return (
    <PageContainer>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />

      <Dialog
        sx={{
          bgcolor: '#FAFAFA',
        }}
        openDialog={openConfirmDialog}
        dialogTitle={''}
        dialogAction={PANEL_FORM_DIALOG_TEXT.CONFIRM}
        cancelAction={PANEL_FORM_DIALOG_TEXT.CANCEL}
        onDialogAction={async () => {
          await handleReleasePanelAllocation(panelId);
        }}
        width="800px"
        onDialogClose={() => {
          setOpenConfirmDialog(false);
        }}
      >
        <div className="bg-white border border-[#DFDFDF] mx-8 flex justify-center items-center px-8 py-4">
          <p className="font-semibold">
            {PAGE_TEXT.PANEL_MANAGEMENT.RELEASE_CONFIRM}
          </p>
        </div>
      </Dialog>

      <div className="p-4">
        <div className="flex w-full justify-between">
          <Button
            variant="outlined"
            onClick={handleBack}
            size="medium"
            startIcon={<Home />}
            sx={{
              textTransform: 'none',
            }}
          >
            {PAGE_TEXT.PANEL_MANAGEMENT.SUBTITLE}
          </Button>
          {isGSview && (
            <FormControlLabel
              control={
                <Switch
                  checked={gsAutoRefresh}
                  onChange={handleToggleGSAutoRefresh}
                />
              }
              label={
                <p
                  className={
                    gsAutoRefresh ? 'text-[#951D1D]' : 'text-[#A2A2A2]'
                  }
                >
                  {PAGE_TEXT.PANEL_MANAGEMENT.AUTO_REFRESH}&nbsp;
                  {gsAutoRefresh ? 'On' : 'Off'}
                </p>
              }
            />
          )}
        </div>
        <h1>{PAGE_TEXT.APPLICATION_LIST.TITLE}</h1>
      </div>

      {loading ? (
        <Loading />
      ) : (
        <>
          <div className="p-4">
            {isGSview && showReviewer && (
              <div className="flex flex-wrap gap-2 items-center">
                <p className="text-[#951D1D] font-semibold">
                  {PAGE_TEXT.PANEL_MANAGEMENT.ALLOCATE_TEXT}
                </p>
                <Button
                  variant="contained"
                  disabled={allocationReleased}
                  onClick={async () => {
                    await handleGetPanelApplicationInitialAllocation(panelId);
                  }}
                  size="medium"
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  {PAGE_TEXT.PANEL_MANAGEMENT.ALLOCATE}
                </Button>
                <Button
                  variant="contained"
                  disabled={allocationReleased || !hasAllocatedReviewers}
                  onClick={() => {
                    setOpenConfirmDialog(true);
                  }}
                  size="medium"
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  {PAGE_TEXT.PANEL_MANAGEMENT.RELEASE}
                </Button>
                {allocationReleased && (
                  <p className="italic text-[#B6B6B6] ml-2">
                    {PAGE_TEXT.PANEL_MANAGEMENT.RELEASED_HINT}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="form-area">
            <PanelApplications
              viewSituation={viewSituation}
              panelId={panelId}
              filterLockedFlag={filterLockedFlag}
              allocationReleased={allocationReleased}
              hasAllocatedReviewers={hasAllocatedReviewers}
              panelApplications={panelApplications}
              panelNumbers={panelNumbers}
              panelInformation={panelInformation}
              dimensionsCollection={dimensionsCollection}
              appliedFilter={tableFilter}
              lockedFilter={lockFilter}
              gsAutoRefresh={gsAutoRefresh}
              dualPartnerUniversityList={dualPartnerUniversityList}
              handleApplyTagFilter={handleApplyTagFilter}
              handleLoading={handleLoading}
              handleManageAlert={handleManageAlert}
              refetch={refetch}
            />
          </div>
        </>
      )}
    </PageContainer>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;

  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    text-align: center;
    color: #951d1d;
  }

  .form-area {
    flex: 0.95;
    margin: 0 1rem 1rem 1rem;
  }
`;
